import { Component, inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { Store } from '@ngxs/store';
import { VxSidebarNavComponent } from '@ui/sidebar-nav/sidebar-nav.component';
import { VxSidebarNavGroupComponent } from '@ui/sidebar-nav/sidebar-nav-group.component';
import { VxSidebarNavItemComponent } from '@ui/sidebar-nav/sidebar-nav-item.component';
import { RequiredRolesAnnotation } from 'src/app/core/guards/permission.guard';
import { SharedModule } from 'src/app/core/shared.module';
import { AuthorizedMenuItem } from 'src/app/models/permission';
import { MenuService } from 'src/app/services/menu/menu.service';

// factory, not a shared const: MenuService.filterMenuItems mutates the
// nested `items` arrays while filtering, so every component instance needs
// its own copy
const buildItems = (): AuthorizedMenuItem[] => [
  {
    label: 'pages.settings.menu.groups.personal',
    items: [
      {
        label: 'pages.settings.menu.account',
        icon: 'user-circle',
        routerLink: ['/settings/account/general'],
      },
      {
        label: 'pages.settings.menu.security',
        icon: 'lock-simple',
        routerLink: ['/settings/account/security'],
      },
      {
        label: 'pages.settings.menu.notifications',
        icon: 'bell',
        routerLink: ['/settings/account/notifications'],
      },
    ],
  },
  {
    label: 'pages.settings.menu.groups.response',
    items: [
      {
        label: 'pages.settings.menu.integrations',
        icon: 'plugs',
        routerLink: ['/settings/communication/providers'],
        [RequiredRolesAnnotation]: ['notifications.manager'],
      },
      {
        label: 'pages.settings.menu.incidentSchemas',
        icon: 'stack',
        routerLink: ['/settings/incidents/schemas'],
        [RequiredRolesAnnotation]: ['incidentschemas.manager'],
      },
      {
        label: 'pages.settings.menu.incidentRoles',
        icon: 'identification-badge',
        routerLink: ['/settings/incidents/roles'],
        [RequiredRolesAnnotation]: ['incidentroles.manager'],
      },
      {
        label: 'pages.settings.menu.alerting',
        icon: 'siren',
        routerLink: ['/settings/alerting'],
        [RequiredRolesAnnotation]: ['alerts.manager'],
      },
      {
        label: 'pages.settings.menu.broadcastChannels',
        icon: 'megaphone',
        routerLink: ['/settings/incidents/broadcast-channels'],
        [RequiredRolesAnnotation]: ['broadcastchannels.manager'],
      },
    ],
  },
  {
    label: 'pages.settings.menu.groups.organization',
    items: [
      {
        label: 'pages.settings.menu.organization',
        icon: 'buildings',
        routerLink: ['/settings/organization/general'],
        [RequiredRolesAnnotation]: ['organization.manager'],
      },
      {
        label: 'pages.settings.menu.organizationMembers',
        icon: 'users-three',
        routerLink: ['/settings/organization/members'],
        [RequiredRolesAnnotation]: ['organization.manager'],
      },
      {
        label: 'pages.settings.menu.subscription',
        icon: 'credit-card',
        routerLink: ['/settings/organization/subscription'],
        [RequiredRolesAnnotation]: ['subscriptions.manager'],
      },
      {
        label: 'pages.settings.menu.api',
        icon: 'brackets-curly',
        routerLink: ['/settings/organization/api'],
        [RequiredRolesAnnotation]: ['organization.manager'],
      },
    ],
  },
];

@Component({
  selector: 'app-page-settings-shell',
  templateUrl: 'settings.component.html',
  imports: [
    SharedModule,
    RouterOutlet,
    VxSidebarNavComponent,
    VxSidebarNavGroupComponent,
    VxSidebarNavItemComponent,
  ],
})
export class SettingsShellPageComponent {
  private store = inject(Store);

  // group entries without any permitted item are dropped entirely
  protected readonly groups = this.store.selectSignal(
    MenuService.filteredMenuItems(buildItems())
  );
}
